import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { GoogleMap, Marker, InfoWindow, LoadScript } from '@react-google-maps/api';
import { FaTimesCircle, FaCheckCircle } from 'react-icons/fa';

const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '10px',
};

// ChatBot Component
const ChatBot = ({ items = [], userName = 'User', setMapCenter, setBotFilteredItems, isAdmin, users }) => {
  const welcomeMessage = isAdmin
    ? `Welcome Admin ${userName}! You can manage all users' reported items.`
    : `Hello ${userName}! How can I help you today?`;

  const [messages, setMessages] = useState([{ from: 'bot', text: welcomeMessage }]);
  const [input, setInput] = useState('');

  const handleSend = () => {
    if (!input) return;
    const newMessages = [...messages, { from: 'user', text: input }];
    const lower = input.toLowerCase();
    let filtered = [];
    let reply = isAdmin
      ? "Admin commands: 'Show all items', 'Show items by [user email]', 'Show lost items', 'Show found items', 'Show claimed items', 'Show all users', 'Filter by location like lost items in Sironi'."
      : "Try commands like: 'Show my lost items', 'Show my found items', 'Show my claimed items', 'Show all items', or include a location like 'lost items in Sironi'.";

    if (items && items.length > 0) {
      // Extract location from command, strict match
      const locationMatch = lower.match(/in ([a-zA-Z0-9\s@.]+)/);
      const locationFilter = locationMatch ? locationMatch[1].trim() : null;

      // Type/status filter
      if (lower.includes('lost')) filtered = items.filter(i => i.type === 'Lost');
      else if (lower.includes('found')) filtered = items.filter(i => i.type === 'Found' && !i.claimStatus);
      else if (lower.includes('claim') || lower.includes('claims') || lower.includes('claimed')) filtered = items.filter(i => i.type === 'Claimed');
      else if (lower.includes('all')) filtered = items;

      // Admin-specific: filter by user email
      if (isAdmin && lower.includes('by')) {
        const emailMatch = lower.match(/by ([a-zA-Z0-9@.]+)/);
        if (emailMatch) filtered = items.filter(i => i.userEmail === emailMatch[1]);
      }

      // Admin command to show all users
      if (isAdmin && lower.includes('show all users')) {
        if (users.length > 0) {
          reply = (
            <div>
              <strong>All Users:</strong>
              <ul>
                {users.map(u => (
                  <li key={u.id}>{u.name || u.email}</li>
                ))}
              </ul>
            </div>
          );
        } else {
          reply = 'No users found in the system.';
        }
      } else {
        // Strict location filter
        if (locationFilter) {
          const locLower = locationFilter.toLowerCase();
          filtered = filtered.filter(i => i.location?.toLowerCase().trim() === locLower);
        }

        setBotFilteredItems(filtered);

        const first = filtered.find(i => i.locationLat && i.locationLng);
        if (first) setMapCenter({ lat: first.locationLat, lng: first.locationLng });

        if (filtered.length > 0) {
          let emoji = '';
          if (lower.includes('lost')) emoji = '🙂';
          else if (lower.includes('found')) emoji = '🎉';
          else if (lower.includes('claim') || lower.includes('claims') || lower.includes('claimed')) emoji = '👍';
          reply = `Found ${filtered.length} items${locationFilter ? ` in ${locationFilter}` : ''} ${emoji}`;
        } else if (!lower.includes('show all users')) {
          reply = `No items found${locationFilter ? ` in ${locationFilter}` : ''}. 😢`;
        }
      }
    }

    setMessages([...newMessages, { from: 'bot', text: reply }]);
    setInput('');
  };

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '10px', padding: '1rem', marginTop: '2rem' }}>
      <h3>🤖 ChatBot Assistant</h3>
      <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '1rem', background: '#f7f7f7', padding: '0.5rem', borderRadius: '5px' }}>
        {messages.map((m, idx) => (
          <p key={idx} style={{ textAlign: m.from === 'bot' ? 'left' : 'right', margin: '0.2rem 0' }}>
            <strong>{m.from === 'bot' ? 'Bot' : userName}:</strong> {typeof m.text === 'string' ? m.text : m.text}
          </p>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Type your message..."
          style={{ flex: 1, padding: '0.5rem', borderRadius: '5px', border: '1px solid #ccc' }}
        />
        <button onClick={handleSend} style={{ padding: '0.5rem 1rem', borderRadius: '5px', background: '#007bff', color: '#fff', border: 'none', cursor: 'pointer' }}>
          Send
        </button>
      </div>
    </div>
  );
};

// Main ReportedItems Component
const ReportedItems = () => {
  const [userEmail, setUserEmail] = useState('');
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]); // List of all users for admin
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [botFilteredItems, setBotFilteredItems] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        setUserEmail(user.email);
        setIsAdmin(user.email === 'morinea@usiu.ac.ke'); // Admin check
      } else {
        setUserEmail('');
        setItems([]);
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!userEmail) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const lostQuery = isAdmin
          ? collection(db, 'lostItems')
          : query(collection(db, 'lostItems'), where('userEmail', '==', userEmail));
        const foundQuery = isAdmin
          ? collection(db, 'foundItems')
          : query(collection(db, 'foundItems'), where('userEmail', '==', userEmail));
        const claimsQuery = isAdmin
          ? collection(db, 'claims')
          : query(collection(db, 'claims'), where('userEmail', '==', userEmail));

        const [lostSnapshot, foundSnapshot, claimsSnapshot] = await Promise.all([
          getDocs(lostQuery),
          getDocs(foundQuery),
          getDocs(claimsQuery)
        ]);

        const lostItems = lostSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'Lost' }));
        const foundItems = foundSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'Found' }));
        const claims = claimsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'Claimed', claimStatus: doc.data().status || 'Pending' }));

        // Merge claims into found items
        const foundWithClaims = foundItems.map(fItem => {
          const claim = claims.find(c => c.foundItemId === fItem.id);
          if (claim) return { ...fItem, claimStatus: claim.claimStatus, type: 'Claimed' };
          return fItem;
        });
        const claimOnlyItems = claims.filter(c => !foundItems.some(f => f.id === c.foundItemId));

        setItems([...lostItems, ...foundWithClaims, ...claimOnlyItems]);

        // Fetch all users for admin
        if (isAdmin) {
          const usersSnapshot = await getDocs(collection(db, 'users')); // Ensure 'users' collection exists
          const allUsers = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setUsers(allUsers);
        }

        const firstMarker = [...lostItems, ...foundWithClaims, ...claimOnlyItems].find(i => i.locationLat && i.locationLng);
        if (firstMarker) setMapCenter({ lat: firstMarker.locationLat, lng: firstMarker.locationLng });

      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };

    fetchData();
  }, [userEmail, isAdmin]);

  const getRecoveryLikelihood = item => {
    const status = item.claimStatus || item.itemStatus || 'Pending';
    if (status === 'Found' || status === 'Approved') return 90;
    if (status === 'Rejected') return 0;
    return 50;
  };

  const markers = botFilteredItems.filter(item => item.locationLat && item.locationLng);

  if (!userEmail) return <p>Please log in to view your reported items.</p>;

  return (
    <div style={{ padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
      {loading ? (
        <p>Loading items...</p>
      ) : (
        <>
          <ChatBot
            items={items}
            userName={userEmail.split('@')[0]}
            setMapCenter={setMapCenter}
            setBotFilteredItems={setBotFilteredItems}
            isAdmin={isAdmin}
            users={users}
          />

          {botFilteredItems.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
              {botFilteredItems.map(item => {
                const likelihood = getRecoveryLikelihood(item);
                return (
                  <div key={item.id} style={{ background: '#f9f9f9', borderRadius: '10px', padding: '1rem', border: '1px solid #ddd', boxShadow: '0 3px 6px rgba(0,0,0,0.1)' }}>
                    <h3>
                      {item.itemName || 'Unnamed Item'}{' '}
                      {item.type === 'Lost' ? <FaTimesCircle color="red" /> : <FaCheckCircle color={item.claimStatus ? 'blue' : 'green'} />}
                    </h3>
                    {item.type && <p><strong>Type:</strong> {item.type}</p>}
                    {item.claimStatus && <p><strong>Status:</strong> {item.claimStatus}</p>}
                    {item.location && <p><strong>Location:</strong> {item.location}</p>}
                    <p><strong>Predicted Recovery Likelihood:</strong> {likelihood}%</p>
                    {isAdmin && item.userEmail && <p><strong>Reported by:</strong> {item.userEmail}</p>}
                  </div>
                );
              })}
            </div>
          )}

          {markers.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h3>📍 Item Locations</h3>
              <LoadScript googleMapsApiKey="YOUR_GOOGLE_MAPS_API_KEY">
                <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={13}>
                  {markers.map(marker => (
                    <Marker
                      key={marker.id}
                      position={{ lat: marker.locationLat, lng: marker.locationLng }}
                      onClick={() => setSelectedMarker(marker)}
                      icon={{
                        url: marker.type === 'Lost'
                          ? "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                          : marker.claimStatus
                            ? "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                            : "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                      }}
                    />
                  ))}
                  {selectedMarker && (
                    <InfoWindow position={{ lat: selectedMarker.locationLat, lng: selectedMarker.locationLng }} onCloseClick={() => setSelectedMarker(null)}>
                      <div>
                        <strong>{selectedMarker.itemName}</strong><br />
                        Type: {selectedMarker.type}<br />
                        Status: {selectedMarker.claimStatus || selectedMarker.itemStatus || 'Pending'}<br />
                        Predicted Recovery: {getRecoveryLikelihood(selectedMarker)}%<br />
                        {isAdmin && selectedMarker.userEmail && <span>Reported by: {selectedMarker.userEmail}</span>}
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </LoadScript>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportedItems;
